import { useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { CalendarEventInput, CalendarEventRecord } from '@shared/schemas';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useCreateEvent,
  useDeleteEvent,
  useEvents,
  useSeasons,
  useTeams,
  useUpdateEvent,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import {
  addMonths,
  isInMonth,
  matchDayLabel,
  monthGrid,
  monthLabel,
  todayISO,
  weekdayLabels,
} from '@/lib/calendar';
import { formatDate } from '@/lib/format';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Chip } from '@/ui/Chip';

const KINDS = ['match', 'friendly', 'training', 'test', 'recovery', 'meeting', 'trial', 'other'] as const;
const STATUSES = ['scheduled', 'postponed', 'cancelled', 'played'] as const;

/**
 * Each kind gets a distinct mark. Matches and friendlies borrow the top of the
 * intensity ramp because they are the week's peak; training and recovery sit at
 * the calm end. Everything else stays neutral so a month of fixtures reads at a
 * glance without a legend.
 */
/** The same vocabulary as a solid mark, for a cell too narrow to hold words. */
const KIND_DOT: Record<string, string> = {
  match: 'bg-load-peak',
  friendly: 'bg-load-high',
  training: 'bg-load-low',
  test: 'bg-load-moderate',
  recovery: 'bg-load-rest',
  meeting: 'bg-line-strong',
  trial: 'bg-line-strong',
  other: 'bg-line-strong',
};

const KIND_STYLE: Record<string, string> = {
  match: 'bg-load-peak/12 text-load-peak',
  friendly: 'bg-load-high/12 text-load-high',
  training: 'bg-load-low/12 text-load-low',
  test: 'bg-load-moderate/14 text-load-moderate',
  recovery: 'bg-load-rest/12 text-load-rest',
  meeting: 'bg-surface-sunken text-ink-muted',
  trial: 'bg-surface-sunken text-ink-muted',
  other: 'bg-surface-sunken text-ink-muted',
};

function blankEvent(teamId: string, onDate: string): CalendarEventInput {
  return { teamId, kind: 'match', onDate, status: 'scheduled' } as CalendarEventInput;
}

export function Calendar({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const teamList = teams.data ?? [];

  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const today = todayISO();
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return { year: now.getUTCFullYear(), month: now.getUTCMonth() };
  });

  const grid = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);
  const from = grid[0]?.[0];
  const to = grid[5]?.[6];

  const events = useEvents(activeTeamId ?? undefined, from, to);
  const seasons = useSeasons(activeTeamId ?? undefined);
  const create = useCreateEvent();
  const update = useUpdateEvent();
  const remove = useDeleteEvent();
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [editing, setEditing] = useState<CalendarEventRecord | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CalendarEventInput>(() => blankEvent('', today));

  const list = events.data ?? [];

  const byDate = useMemo(() => {
    const map = new Map<string, CalendarEventRecord[]>();
    for (const e of list) {
      const bucket = map.get(e.onDate) ?? [];
      bucket.push(e);
      map.set(e.onDate, bucket);
    }
    return map;
  }, [list]);

  // Cancelled fixtures must not anchor the week: MD-2 measured against a match
  // that is not being played would misdescribe every session around it.
  const matchDates = useMemo(
    () =>
      list
        .filter((e) => (e.kind === 'match' || e.kind === 'friendly') && e.status !== 'cancelled')
        .map((e) => e.onDate),
    [list],
  );

  const set = <K extends keyof CalendarEventInput>(k: K) => (value: CalendarEventInput[K]) =>
    setForm((f) => ({ ...f, [k]: value }));

  const startCreate = (onDate: string) => {
    if (!activeTeamId) return;
    setEditing(null);
    setForm(blankEvent(activeTeamId, onDate));
    setOpen(true);
  };

  const startEdit = (e: CalendarEventRecord) => {
    setEditing(e);
    setForm({
      teamId: e.teamId,
      seasonId: e.seasonId ?? undefined,
      kind: e.kind as CalendarEventInput['kind'],
      title: e.title ?? undefined,
      onDate: e.onDate,
      startsAt: e.startsAt ?? undefined,
      endsAt: e.endsAt ?? undefined,
      location: e.location ?? undefined,
      opponent: e.opponent ?? undefined,
      venue: (e.venue as CalendarEventInput['venue']) ?? undefined,
      competition: e.competition ?? undefined,
      status: e.status as CalendarEventInput['status'],
      changeReason: e.changeReason ?? undefined,
      notes: e.notes ?? undefined,
    });
    setOpen(true);
  };

  const describe = (e: CalendarEventRecord) =>
    e.title ||
    (e.opponent ? `${t('calendar.vs')} ${e.opponent}` : t(`calendar.kind_${e.kind}`));

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
        title={t('calendar.title')}
        lead={t('calendar.lead')}
        help={localePath(locale, '/guide/club')}
      />
        <Panel title={t('calendar.title')}>
          <EmptyState title={t('teams.empty')} body={t('calendar.noTeam')} />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('calendar.title')}
        lead={t('calendar.lead')}
        help={localePath(locale, '/guide/club')}
        action={
          <Button variant="primary" onClick={() => startCreate(today)}>
            {t('calendar.add')}
          </Button>
        }
      />

      {teamList.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1">
          {teamList.map((tm) => (
            <button
              key={tm.id}
              type="button"
              onClick={() => setTeamId(tm.id)}
              aria-pressed={tm.id === activeTeamId}
              className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                tm.id === activeTeamId
                  ? 'border-ink bg-ink text-paper'
                  : 'border-line bg-surface text-ink-muted hover:border-ink hover:text-ink'
              }`}
            >
              {tm.name}
            </button>
          ))}
        </div>
      ) : null}

      {open ? (
        <div className="mb-5">
          <Panel title={editing ? t('calendar.edit') : t('calendar.add')}>
            <form
              noValidate
              className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-2 lg:grid-cols-3"
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await submit(async () => {
                  if (editing) await update.mutateAsync({ eventId: editing.id, input: form });
                  else await create.mutateAsync(form);
                });
                if (ok) {
                  setOpen(false);
                  setEditing(null);
                }
              }}
            >
              {formError ? (
                <div className="sm:col-span-2 lg:col-span-3">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              <Field label={t('calendar.kind')} error={fieldError('kind')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.kind}
                    onChange={(e) => set('kind')(e.target.value as CalendarEventInput['kind'])}
                  >
                    {KINDS.map((k) => (
                      <option key={k} value={k}>
                        {t(`calendar.kind_${k}`)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              <Field label={t('calendar.onDate')} error={fieldError('onDate')}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="date"
                    value={form.onDate}
                    onChange={(e) => set('onDate')(e.target.value)}
                    required
                  />
                )}
              </Field>

              <Field label={t('calendar.status')} error={fieldError('status')}>
                {(p) => (
                  <SelectInput
                    {...p}
                    value={form.status}
                    onChange={(e) => set('status')(e.target.value as CalendarEventInput['status'])}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`calendar.status_${s}`)}
                      </option>
                    ))}
                  </SelectInput>
                )}
              </Field>

              {form.kind === 'match' || form.kind === 'friendly' ? (
                <>
                  <Field label={t('calendar.opponent')} optional error={fieldError('opponent')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        value={form.opponent ?? ''}
                        onChange={(e) => set('opponent')(e.target.value || undefined)}
                      />
                    )}
                  </Field>
                  <Field label={t('calendar.venue')} optional error={fieldError('venue')}>
                    {(p) => (
                      <SelectInput
                        {...p}
                        value={form.venue ?? ''}
                        onChange={(e) =>
                          set('venue')((e.target.value || undefined) as CalendarEventInput['venue'])
                        }
                      >
                        <option value="">{t('squad.none')}</option>
                        <option value="home">{t('calendar.venue_home')}</option>
                        <option value="away">{t('calendar.venue_away')}</option>
                        <option value="neutral">{t('calendar.venue_neutral')}</option>
                      </SelectInput>
                    )}
                  </Field>
                  <Field label={t('calendar.competition')} optional error={fieldError('competition')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        value={form.competition ?? ''}
                        onChange={(e) => set('competition')(e.target.value || undefined)}
                      />
                    )}
                  </Field>
                </>
              ) : (
                <Field
                  label={t('calendar.title_field')}
                  hint={t('calendar.titleHint')}
                  optional
                  error={fieldError('title')}
                >
                  {(p) => (
                    <TextInput
                      {...p}
                      value={form.title ?? ''}
                      onChange={(e) => set('title')(e.target.value || undefined)}
                    />
                  )}
                </Field>
              )}

              <Field label={t('calendar.startsAt')} optional error={fieldError('startsAt')}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="time"
                    value={form.startsAt ?? ''}
                    onChange={(e) => set('startsAt')(e.target.value || undefined)}
                  />
                )}
              </Field>

              <Field label={t('calendar.endsAt')} optional error={fieldError('endsAt')}>
                {(p) => (
                  <TextInput
                    {...p}
                    type="time"
                    value={form.endsAt ?? ''}
                    onChange={(e) => set('endsAt')(e.target.value || undefined)}
                  />
                )}
              </Field>

              <Field label={t('calendar.location')} optional error={fieldError('location')}>
                {(p) => (
                  <TextInput
                    {...p}
                    value={form.location ?? ''}
                    onChange={(e) => set('location')(e.target.value || undefined)}
                  />
                )}
              </Field>

              {seasons.data && seasons.data.length > 0 ? (
                <Field label={t('seasons.title')} optional>
                  {(p) => (
                    <SelectInput
                      {...p}
                      value={form.seasonId ?? ''}
                      onChange={(e) => set('seasonId')(e.target.value || undefined)}
                    >
                      <option value="">{t('squad.none')}</option>
                      {seasons.data!.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </SelectInput>
                  )}
                </Field>
              ) : null}

              {form.status !== 'scheduled' ? (
                <div className="sm:col-span-2 lg:col-span-3">
                  <Field
                    label={t('calendar.changeReason')}
                    hint={t('calendar.changeReasonHint')}
                    error={fieldError('changeReason')}
                  >
                    {(p) => (
                      <TextInput
                        {...p}
                        value={form.changeReason ?? ''}
                        onChange={(e) => set('changeReason')(e.target.value || undefined)}
                      />
                    )}
                  </Field>
                </div>
              ) : null}

              <div className="sm:col-span-2 lg:col-span-3">
                <Field label={t('calendar.notes')} optional error={fieldError('notes')}>
                  {(p) => (
                    <TextArea
                      {...p}
                      value={form.notes ?? ''}
                      onChange={(e) => set('notes')(e.target.value || undefined)}
                    />
                  )}
                </Field>
              </div>

              <div className="flex flex-wrap gap-2 sm:col-span-2 lg:col-span-3">
                <Button type="submit" variant="primary" busy={busy}>
                  {t('calendar.save')}
                </Button>
                <Button
                  type="button"
                  variant="quiet"
                  onClick={() => {
                    setOpen(false);
                    setEditing(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                {editing ? (
                  <Button
                    type="button"
                    variant="danger"
                    className="ms-auto"
                    onClick={async () => {
                      if (!confirm(t('calendar.deleteConfirm'))) return;
                      await remove.mutateAsync(editing.id);
                      setOpen(false);
                      setEditing(null);
                    }}
                  >
                    {t('calendar.delete')}
                  </Button>
                ) : null}
              </div>
            </form>
          </Panel>
        </div>
      ) : null}

      <Panel
        title={monthLabel(cursor.year, cursor.month, locale)}
        action={
          <div className="flex gap-1">
            <Button
              variant="secondary"
              className="!min-w-11 !px-3 !text-meta"
              onClick={() => setCursor((c) => addMonths(c.year, c.month, -1))}
              aria-label={t('common.back')}
            >
              ‹
            </Button>
            <Button
              variant="secondary"
              className="!text-meta"
              onClick={() => {
                const now = new Date();
                setCursor({ year: now.getUTCFullYear(), month: now.getUTCMonth() });
              }}
            >
              {t('calendar.today')}
            </Button>
            <Button
              variant="secondary"
              className="!min-w-11 !px-3 !text-meta"
              onClick={() => setCursor((c) => addMonths(c.year, c.month, 1))}
              aria-label={t('common.close')}
            >
              ›
            </Button>
          </div>
        }
      >
        {events.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            {/* No 576px floor. Seven columns divide 390px into 55px cells,
                which is room for a date and a mark — and the alternative was
                the only unprefixed multi-column grid in the product sitting
                behind a sideways scroller. */}
            <div className="min-w-0 md:min-w-[36rem]">
              <div className="grid grid-cols-7 border-b border-line bg-surface-sunken">
                {weekdayLabels(locale).map((d) => (
                  <div key={d} className="px-2 py-1.5 text-meta text-ink-faint">
                    {d}
                  </div>
                ))}
              </div>
              {grid.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7 border-b border-line last:border-b-0">
                  {week.map((date) => {
                    const dayEvents = byDate.get(date) ?? [];
                    const inMonth = isInMonth(date, cursor.year, cursor.month);
                    const isToday = date === today;
                    const md = matchDayLabel(date, matchDates);
                    return (
                      <button
                        key={date}
                        type="button"
                        onClick={() => startCreate(date)}
                        className={`min-h-24 border-e border-line p-1.5 text-start align-top transition-colors last:border-e-0 hover:bg-surface-sunken ${
                          inMonth ? '' : 'bg-surface-sunken/60 text-ink-faint'
                        } ${isToday ? 'bg-signal-soft' : ''}`}
                      >
                        {/* Stacked on a phone: a 55px cell cannot hold "23"
                            and "MD-1" on one line, and side by side they broke
                            across two anyway — with the last column clipping
                            its label against the edge. */}
                        <span className="flex flex-col items-start gap-0 sm:flex-row sm:items-baseline sm:gap-1.5">
                          <span
                            className={`tabular font-condensed text-meta ${
                              isToday ? 'font-bold text-ink' : 'text-ink-muted'
                            }`}
                          >
                            {Number(date.slice(8, 10))}
                          </span>
                          {md && !dayEvents.some((e) => e.kind === 'match' || e.kind === 'friendly') ? (
                            <span className="tabular font-condensed text-[0.6875rem] text-ink-faint">
                              {md}
                            </span>
                          ) : null}
                        </span>
                        {/* A mark per event on a phone. The chip below is a
                            17px target nested inside the day's own, which on a
                            touch screen means neither is reliable — so the day
                            is the only thing to tap, and it opens. */}
                        <span className="mt-1 flex flex-wrap gap-1 md:hidden">
                          {dayEvents.map((e) => (
                            <span
                              key={e.id}
                              aria-hidden="true"
                              className={`block size-2 rounded-full ${
                                KIND_DOT[e.kind] ?? KIND_DOT.other
                              } ${e.status === 'cancelled' ? 'opacity-40' : ''}`}
                            />
                          ))}
                        </span>
                        <span className="mt-1 hidden gap-0.5 md:grid">
                          {dayEvents.slice(0, 3).map((e) => (
                            <span
                              key={e.id}
                              onClick={(ev) => {
                                ev.stopPropagation();
                                startEdit(e);
                              }}
                              className={`block truncate rounded-[3px] px-1 py-0.5 text-[0.6875rem] ${
                                KIND_STYLE[e.kind] ?? KIND_STYLE.other
                              } ${e.status === 'cancelled' ? 'line-through opacity-60' : ''}`}
                            >
                              {e.startsAt ? <span className="tabular me-1">{e.startsAt}</span> : null}
                              {describe(e)}
                            </span>
                          ))}
                          {dayEvents.length > 3 ? (
                            <span className="ps-1 text-[0.6875rem] text-ink-faint">
                              +{dayEvents.length - 3}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </Panel>

      {list.length === 0 && !events.isLoading ? (
        <div className="mt-5">
          <Panel title={t('calendar.title')}>
            <EmptyState
              title={t('calendar.empty')}
              body={t('calendar.emptyBody')}
              action={
                <Button variant="primary" onClick={() => startCreate(today)}>
                  {t('calendar.add')}
                </Button>
              }
            />
          </Panel>
        </div>
      ) : (
        <p className="mt-3 text-meta text-ink-faint">{t('calendar.mdHint')}</p>
      )}

      {list.length > 0 ? (
        <div className="mt-5">
          <Panel title={t('calendar.list')} count={list.length}>
            <ul className="divide-y divide-line">
              {list.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => startEdit(e)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-start hover:bg-surface-sunken"
                  >
                    <span className="tabular w-24 shrink-0 text-meta text-ink-muted">
                      {formatDate(e.onDate, locale)}
                    </span>
                    <span
                      className={`rounded-[3px] px-1.5 py-0.5 text-meta ${
                        KIND_STYLE[e.kind] ?? KIND_STYLE.other
                      }`}
                    >
                      {t(`calendar.kind_${e.kind}`)}
                    </span>
                    <span
                      className={`min-w-0 flex-1 truncate ${
                        e.status === 'cancelled' ? 'text-ink-faint line-through' : ''
                      }`}
                    >
                      {describe(e)}
                      {e.venue ? (
                        <span className="ms-2 text-meta text-ink-faint">
                          {t(`calendar.venue_${e.venue}`)}
                        </span>
                      ) : null}
                    </span>
                    {e.startsAt ? (
                      <span className="tabular text-meta text-ink-muted">{e.startsAt}</span>
                    ) : null}
                    {e.status !== 'scheduled' ? (
                      <Chip muted>{t(`calendar.status_${e.status}`)}</Chip>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      ) : null}
    </>
  );
}
